import type { PaginatedResponse } from '@repo/types/pagination';
import type { ReactNode } from 'react';

import { TriangleAlertIcon } from 'lucide-react';
import { Link } from 'react-router';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { EmptyState, EmptyStateContent, EmptyStateDescription } from '@/components/ui/empty-state';
import { Skeleton } from '@/components/ui/skeleton';

const PREVIEW_SIZE = 5;

interface ParentGroupPreviewSectionProps<TItem> {
  title: string;
  moreHref: string;
  data: PaginatedResponse<TItem> | undefined;
  isPending: boolean;
  isError?: boolean;
  emptyMessage: string;
  children: (rows: TItem[]) => ReactNode;
}

export function ParentGroupPreviewSection<TItem>(parentGroupPreviewSectionProps: ParentGroupPreviewSectionProps<TItem>) {
  const { title, moreHref, data, isPending, isError = false, emptyMessage, children } = parentGroupPreviewSectionProps;

  return (
    <section className={'flex flex-col gap-2'}>
      <div className={'flex items-center justify-between gap-2'}>
        <h3 className={'text-foreground text-sm font-semibold'}>{title}</h3>
        <Button asChild variant={'outline'} size={'sm'}>
          <Link to={moreHref}>Zobacz więcej</Link>
        </Button>
      </div>

      {isPending ? (
        <Skeleton className={'h-20 w-full'} />
      ) : isError ? (
        <Alert variant={'warning'}>
          <TriangleAlertIcon />
          <AlertDescription>Nie udało się pobrać tych danych. Sprawdź, czy konto jest nadal powiązane.</AlertDescription>
        </Alert>
      ) : !data || data.total === 0 ? (
        <EmptyState>
          <EmptyStateContent>
            <EmptyStateDescription>{emptyMessage}</EmptyStateDescription>
          </EmptyStateContent>
        </EmptyState>
      ) : (
        children(data.data.slice(0, PREVIEW_SIZE))
      )}
    </section>
  );
}
